import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import {
  applyFixedWingLineAction,
  assignFixedWingLineCrewByDate,
  createFixedWingLine,
  deleteFixedWingLine,
  getFixedWingCalendar,
  listFixedWingEligibleCrew,
  listFixedWingLines,
  moveFixedWingLineCrew,
  removeFixedWingLineCrew,
  updateFixedWingLine,
} from '../../../api/scheduler/functions';
import { AssignFixedWingLineCrewByDateRequest } from '../../../api/scheduler/models/assign-fixed-wing-line-crew-by-date-request';
import { CreateFixedWingLineRequest } from '../../../api/scheduler/models/create-fixed-wing-line-request';
import { FixedWingLineAction } from '../../../api/scheduler/models/fixed-wing-line-action';
import { UpdateFixedWingLineRequest } from '../../../api/scheduler/models/update-fixed-wing-line-request';
import { SchedulerApiConfiguration } from '../../../api/scheduler/scheduler-api-configuration';
import {
  FixedWingCrewRemoval,
  FixedWingEligibleCrew,
  FixedWingLine,
  FixedWingLineAssignment,
  FixedWingSavedLine,
} from '../models/fixed-wing.models';
import {
  mapFixedWingEligibleCrewDto,
  mapFixedWingLineDto,
  mapFixedWingLineOptionDto,
} from '../utils/fixed-wing.mapper';

@Injectable({ providedIn: 'root' })
export class FixedWingApiService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(SchedulerApiConfiguration);

  listLines(dateFrom: string, dateTo: string): Observable<FixedWingLine[]> {
    return getFixedWingCalendar(this.http, this.config.rootUrl, { dateFrom, dateTo }).pipe(
      map((res) => (res.body.data?.days ?? []).flatMap((day) => day.lines.map(mapFixedWingLineDto)))
    );
  }

  /** Saved lines of one date. Never includes unsaved client defaults. */
  listLinesForDate(date: string): Observable<FixedWingSavedLine[]> {
    return listFixedWingLines(this.http, this.config.rootUrl, { date }).pipe(
      map((res) => (res.body.data ?? []).map(mapFixedWingLineOptionDto))
    );
  }

  createLine(body: CreateFixedWingLineRequest): Observable<FixedWingLine> {
    return createFixedWingLine(this.http, this.config.rootUrl, { body }).pipe(
      map((res) => mapFixedWingLineDto(res.body.data!))
    );
  }

  updateLine(id: number, body: UpdateFixedWingLineRequest): Observable<FixedWingLine> {
    return updateFixedWingLine(this.http, this.config.rootUrl, { id, body }).pipe(
      map((res) => mapFixedWingLineDto(res.body.data!))
    );
  }

  deleteLine(id: number): Observable<void> {
    return deleteFixedWingLine(this.http, this.config.rootUrl, { id }).pipe(map(() => undefined));
  }

  listEligibleCrew(date: string): Observable<FixedWingEligibleCrew[]> {
    return listFixedWingEligibleCrew(this.http, this.config.rootUrl, { date }).pipe(
      map((res) => (res.body.data ?? []).map(mapFixedWingEligibleCrewDto))
    );
  }

  /**
   * Assigns crew to a saved line (`lineId`) or saves a client default (`name` + `order`)
   * and assigns in one request. The caller sends one of those, never both.
   */
  assignCrew(body: AssignFixedWingLineCrewByDateRequest): Observable<FixedWingLineAssignment> {
    return assignFixedWingLineCrewByDate(this.http, this.config.rootUrl, { body }).pipe(
      map((res) => ({
        lineCreated: res.body.data!.lineCreated,
        line: mapFixedWingLineDto(res.body.data!.line),
      }))
    );
  }

  moveCrew(
    sourceLineId: number,
    crewId: number,
    targetLineId: number
  ): Observable<FixedWingLine[]> {
    return moveFixedWingLineCrew(this.http, this.config.rootUrl, {
      id: sourceLineId,
      body: { crewId, targetLineId },
    }).pipe(map((res) => (res.body.data?.lines ?? []).map(mapFixedWingLineDto)));
  }

  removeCrew(id: number, crewIds: number[]): Observable<FixedWingCrewRemoval> {
    return removeFixedWingLineCrew(this.http, this.config.rootUrl, {
      id,
      body: { crewIds },
    }).pipe(
      map((res) => ({
        lineRemoved: res.body.data!.lineRemoved,
        line: res.body.data!.line ? mapFixedWingLineDto(res.body.data!.line) : null,
      }))
    );
  }

  applyLineAction(id: number, action: FixedWingLineAction): Observable<FixedWingLine> {
    return applyFixedWingLineAction(this.http, this.config.rootUrl, {
      id,
      body: { action },
    }).pipe(map((res) => mapFixedWingLineDto(res.body.data!)));
  }
}
